/**
 * What a meeting looks like once you are in it.
 *
 * Two layouts, one rule. With nobody sharing, everyone gets the same card in
 * one regular grid — with a camera or without, so nobody becomes a floating
 * rectangle in a corner. With a screen going out, the surface takes the stage
 * and the faces become a rail beside it: the screen is the content, the faces
 * are the context. See _design/sketches/Meetings.md §4.
 *
 * Both layouts are the same list of cards under the same parent. Only their
 * styling changes, so a video element is never taken down and put up again
 * because somebody started sharing or opened the people panel.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactElement, ReactNode } from 'react'

import type { Call, CallShareSource, UUID } from '@/api/types'
import { describePeer, type PeerState } from '@/hooks/callPeers'
import type { CallSessionState } from '@/hooks/useCallSession'
import { Avatar, Button } from '@/components/ui'
import { unreachableNotice } from '@/lib/callCopy'
import { fitTile, joinOrder, stageColumns, tileAccessibleName } from '@/lib/callStage'
import styles from './CallWindow.module.css'

/**
 * `describePeer` answers in tokens, not in words. The words live here, once,
 * so the tile and the people panel cannot drift into saying different things
 * about the same connection.
 */
export function peerStateLabel(state: PeerState | null | undefined): string | null {
  switch (state) {
    case 'connecting':
      return 'Connecting…'
    case 'reconnecting':
      return 'Reconnecting…'
    case 'failed':
      return 'Could not connect'
    default:
      return null
  }
}

export function participantName(call: Call, userId: UUID): string {
  const person = call.participants.find((participant) => participant.user?.id === userId)?.user
  if (person === undefined || person === null) return 'Someone'
  const display = person.display_name.trim()
  return display === '' ? person.username : display
}

function MicOffIcon(): ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <rect x="7.6" y="2.8" width="4.8" height="9" rx="2.4" />
      <path d="M4.8 9.4a5.2 5.2 0 0 0 10.4 0M10 14.6V17" />
      <path d="M3.6 3.4 16.4 16.6" />
    </svg>
  )
}

function ScreenIcon(): ReactElement {
  return (
    <svg width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.6" y="4.4" width="14.8" height="9.8" rx="2" />
      <path d="M7.4 17h5.2" />
    </svg>
  )
}

/**
 * A picture that follows whatever stream it is handed. Always silent: what
 * people say is played by the provider, which outlives the stage, so putting
 * the window away does not put the meeting on mute.
 */
function Video({
  stream,
  className,
  mirrored = false,
}: {
  stream: MediaStream | null
  className?: string
  mirrored?: boolean
}): ReactElement {
  const ref = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    if (ref.current === null) return
    ref.current.srcObject = stream
  }, [stream])

  return <video ref={ref} className={className} data-self={mirrored} autoPlay playsInline muted />
}

export interface TileProps {
  name: string
  stream: MediaStream | null
  /** Your own tile: mirrored. */
  self?: boolean
  muted?: boolean
  speaking?: boolean
  sharing?: boolean
  state?: PeerState | null
  userId?: UUID
  compact?: boolean
  /** Never came up, even after an ICE restart. */
  unreachable?: boolean
  /** In the rail, past the four that are shown. Kept mounted, not drawn. */
  hidden?: boolean
  onRetry?: (userId: UUID) => void
}

/**
 * One person. Everyone gets this card, camera or no camera; state is said on
 * the card it belongs to rather than as a banner over the whole meeting.
 */
export function CallTile({
  name,
  stream,
  self = false,
  muted = false,
  speaking = false,
  sharing = false,
  state = null,
  userId,
  compact = false,
  unreachable = false,
  hidden = false,
  onRetry,
}: TileProps): ReactElement {
  const failed = state === 'failed'
  const label = peerStateLabel(state)

  return (
    <div
      className={styles.tileWrap}
      role="group"
      aria-label={unreachable ? unreachableNotice(name) : tileAccessibleName({ name, muted, speaking, sharing, state })}
      data-speaking={speaking}
      data-sharing={sharing}
      data-hidden={hidden ? 'true' : undefined}
      data-state={failed ? 'failed' : state === 'reconnecting' ? 'reconnecting' : undefined}
    >
      {stream === null ? (
        <div className={styles.tileFallback}>
          <Avatar name={name} id={userId} size={compact ? 'md' : 'lg'} decorative />
        </div>
      ) : (
        <Video stream={stream} className={styles.tile} mirrored={self} />
      )}

      {unreachable ? (
        <div className={styles.tileUnreachable} role="alert">
          <span className={styles.tileUnreachableText}>{unreachableNotice(name)}</span>
          {onRetry === undefined || userId === undefined ? null : (
            <Button size="sm" variant="secondary" onClick={() => onRetry(userId)}>
              Try again
            </Button>
          )}
        </div>
      ) : sharing ? (
        <span className={styles.sharingBadge} aria-hidden="true">
          <ScreenIcon />
          Sharing
        </span>
      ) : label === null ? null : (
        <span className={styles.tileState} data-state={failed ? 'failed' : undefined} aria-hidden="true">
          {label}
        </span>
      )}

      <span className={styles.tileLabel} aria-hidden="true">
        {muted ? (
          <span className={styles.mutedBadge}>
            <MicOffIcon />
          </span>
        ) : null}
        <span className={styles.tileName}>{name}</span>
      </span>
    </div>
  )
}

export interface StageTile {
  id: string
  name: string
  stream: MediaStream | null
  self: boolean
  muted: boolean
  speaking: boolean
  sharing: boolean
  state: PeerState | null
  userId?: UUID
  unreachable: boolean
}

/** A stream with a picture in it. An audio-only peer has a stream and no face. */
function withPicture(stream: MediaStream | undefined): MediaStream | null {
  if (stream === undefined) return null
  return typeof stream.getVideoTracks === 'function' && stream.getVideoTracks().length === 0 ? null : stream
}

/**
 * Everyone in the call: the people the server lists as joined and anyone a
 * connection is held to, then you. `order` is the order they have stood in so
 * far, and it is kept — a joiner is appended, and nobody moves because they
 * spoke, reconnected or started their camera. `phases` overrides what a tile
 * says about its connection with what the clock says (§6).
 */
export function stageTiles(
  call: Call,
  session: CallSessionState,
  selfUserId: UUID | undefined,
  order: readonly UUID[] = [],
  phases?: Record<UUID, PeerState>,
): StageTile[] {
  const sharingUserId = session.share.state?.userId ?? null
  const listed = call.participants
    .filter((participant) => participant.status === 'joined')
    .map((participant) => participant.user?.id)
    .filter((id): id is UUID => id !== undefined && id !== selfUserId)
  const present = [...new Set([...listed, ...Object.keys(session.peerStates)])]

  const remote: StageTile[] = joinOrder(order, present).map((userId) => {
    const media = session.remoteMedia[userId]
    const stream = session.remoteTracks.find((track) => track.userId === userId)?.stream
    return {
      id: userId,
      userId,
      name: participantName(call, userId),
      // A camera that is off is a face, not a black rectangle or the last
      // frame it sent.
      stream: media?.videoEnabled === false ? null : withPicture(stream),
      self: false,
      muted: media?.audioMuted === true,
      speaking: session.speaking[userId] === true,
      sharing: sharingUserId === userId,
      state: phases?.[userId] ?? describePeer(session.peerStates[userId]),
      unreachable: session.unreachable[userId] === true,
    }
  })

  if (session.localStream !== null) {
    remote.push({
      id: 'self',
      userId: selfUserId,
      name: 'You',
      stream: session.cameraEnabled ? withPicture(session.localStream) : null,
      self: true,
      muted: !session.micEnabled,
      speaking: false,
      sharing: sharingUserId !== null && sharingUserId === selfUserId,
      state: null,
      unreachable: false,
    })
  }

  return remote
}

/** What the browser said was picked, in the words people use for it. */
export function surfaceNoun(source: CallShareSource | null | undefined): string {
  switch (source) {
    case 'window':
      return 'window'
    case 'tab':
      return 'tab'
    default:
      return 'screen'
  }
}

/** Under this the stage is a phone: one column to two people, a rail underneath. */
const COMPACT_STAGE_WIDTH = 640
const RAIL_FACES = 4

function useSize(): [(node: HTMLDivElement | null) => void, { width: number; height: number }] {
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  useLayoutEffect(() => {
    if (node === null || typeof ResizeObserver === 'undefined') return undefined
    const read = (): void => {
      const box = node.getBoundingClientRect()
      setSize((current) =>
        current.width === box.width && current.height === box.height
          ? current
          : { width: box.width, height: box.height },
      )
    }
    read()
    const observer = new ResizeObserver(read)
    observer.observe(node)
    return () => observer.disconnect()
  }, [node])

  return [setNode, size]
}

export interface StageSurface {
  stream: MediaStream | null
  presenter: string
  source?: CallShareSource | null
  /** We are the one sharing: the surface is shown small, never full size. */
  own: boolean
  onStop?: () => void
}

/**
 * The stage: a fixed area the cards are fitted into. The grid when nobody is
 * sharing; the surface with the faces as a rail when somebody is.
 */
export function CallStageArea({
  tiles,
  surface = null,
  onRetry,
  children,
}: {
  tiles: StageTile[]
  surface?: StageSurface | null
  onRetry?: (userId: UUID) => void
  /** Laid over the stage, not into it: notices and the people panel. */
  children?: ReactNode
}): ReactElement {
  const [areaRef, area] = useSize()
  const surfaceRef = useRef<HTMLDivElement | null>(null)
  const presenting = surface !== null
  const compact = area.width > 0 && area.width < COMPACT_STAGE_WIDTH
  const gap = compact ? 10 : 14
  const columns = stageColumns(tiles.length, compact)
  const tile = fitTile({ width: area.width, height: area.height, count: tiles.length, columns, gap })

  // The presenter first, then whoever else fits, in the order they stand in.
  const railShown = new Set(
    [...tiles.filter((entry) => entry.sharing), ...tiles.filter((entry) => !entry.sharing)]
      .slice(0, RAIL_FACES)
      .map((entry) => entry.id),
  )
  const railHidden = presenting ? tiles.length - railShown.size : 0

  const tilesStyle: CSSProperties = presenting
    ? {}
    : ({
        '--stage-columns': String(columns),
        // Unmeasured — a test, or the first frame — the columns share the width.
        '--tile-width': tile.width > 0 ? `${tile.width}px` : '1fr',
      } as CSSProperties)

  return (
    <div
      ref={areaRef}
      className={styles.stageArea}
      data-presenting={presenting}
      data-compact={compact}
      data-own={surface?.own === true}
    >
      {surface === null ? null : (
        <div ref={surfaceRef} className={styles.surface}>
          <Video stream={surface.stream} className={styles.surfaceVideo} />
          <span className={styles.surfaceLabel}>
            <ScreenIcon />
            {surface.own
              ? `Your ${surfaceNoun(surface.source)}`
              : `${surface.presenter}’s ${surfaceNoun(surface.source)}`}
          </span>
          <div className={styles.surfaceActions}>
            {surface.own ? (
              surface.onStop === undefined ? null : (
                <Button size="sm" variant="danger" onClick={surface.onStop}>
                  Stop sharing
                </Button>
              )
            ) : (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  const element = surfaceRef.current
                  const doc = element?.ownerDocument ?? document
                  if (doc.fullscreenElement !== null) void doc.exitFullscreen?.()
                  else void element?.requestFullscreen?.().catch(() => undefined)
                }}
              >
                Full screen
              </Button>
            )}
          </div>
        </div>
      )}

      <div className={styles.tiles} data-layout={presenting ? 'rail' : 'grid'} style={tilesStyle}>
        {tiles.map((entry) => (
          <CallTile
            key={entry.id}
            {...entry}
            compact={presenting}
            hidden={presenting && !railShown.has(entry.id)}
            onRetry={onRetry}
          />
        ))}
        {railHidden > 0 ? <div className={styles.railMore}>+{railHidden} more</div> : null}
      </div>

      {children}
    </div>
  )
}

/** The people panel: the same names as the member list, with what they are doing. */
export function CallPeople({
  tiles,
  total,
  onClose,
}: {
  tiles: StageTile[]
  total: number
  onClose: () => void
}): ReactElement {
  return (
    <aside className={styles.people} aria-label="People in the call">
      <div className={styles.peopleHead}>
        <span className={styles.peopleTitle}>People</span>
        <span className={styles.peopleCount}>{total} of 8</span>
        <button type="button" className={styles.pillButton} aria-label="Close the people panel" onClick={onClose}>
          <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M5.4 5.4 14.6 14.6M14.6 5.4 5.4 14.6" />
          </svg>
        </button>
      </div>

      {tiles.map((tile) => (
        <div key={tile.id} className={styles.personRow}>
          <Avatar name={tile.name} id={tile.userId} size="sm" decorative />
          <span className={styles.personName}>{tile.name}</span>
          {tile.sharing ? (
            <span className={styles.personState} data-live="true">
              Sharing
            </span>
          ) : tile.speaking ? (
            <span className={styles.personState} data-live="true">
              Speaking
            </span>
          ) : peerStateLabel(tile.state) !== null ? (
            <span className={styles.personState}>{peerStateLabel(tile.state)}</span>
          ) : null}
          {tile.muted ? (
            <span className={styles.mutedBadge} role="img" aria-label="Microphone off">
              <MicOffIcon />
            </span>
          ) : null}
        </div>
      ))}
    </aside>
  )
}
